import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import API from '../api/axios';

const LockIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#764ba2" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    <circle cx="12" cy="16" r="1" />
  </svg>
);

const EyeIcon = ({ open }) => (
  open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    </svg>
  )
);

const ForcePasswordReset = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, tempPassword } = location.state || {};

  const [form, setForm] = useState({ newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ newPassword: false, confirmPassword: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // This screen is only reachable as part of the temp-password login flow. If a
  // user lands here directly (no credentials in navigation state), send them to login.
  useEffect(() => {
    if (!email || !tempPassword) {
      navigate('/login', { replace: true });
    }
  }, [email, tempPassword, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.newPassword.length < 6) {
      return setError('Password must be at least 6 characters');
    }
    if (form.newPassword !== form.confirmPassword) {
      return setError('Passwords do not match');
    }
    if (form.newPassword === tempPassword) {
      return setError('New password must be different from the temporary password');
    }

    setLoading(true);
    try {
      await API.post('/auth/update-password', {
        email,
        temporaryPassword: tempPassword,
        newPassword: form.newPassword,
      });
      setMessage('Password set successfully. Redirecting to login...');
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', padding: '1rem' }}>
      <div style={{ background: 'var(--bg-secondary)', borderRadius: '16px', padding: '2.5rem', width: '100%', maxWidth: '420px', boxShadow: 'var(--shadow-xl)', border: '1px solid var(--border-color)' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'center' }}><LockIcon /></div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '800', color: 'var(--text-primary)' }}>Set a New Password</h1>
          <p style={{ margin: '0.5rem 0 0', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
            You signed in with a temporary password. Choose a new password to finish setting up your account.
          </p>
        </div>

        {message && (
          <div style={{ background: 'var(--success-light)', color: 'var(--success)', padding: '0.875rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.875rem' }}>
            {message}
          </div>
        )}
        {error && (
          <div style={{ background: 'var(--danger-light)', color: 'var(--danger)', padding: '0.875rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.875rem' }}>
            {error}
          </div>
        )}

        {!message && (
          <form onSubmit={handleSubmit}>
            {[
              { label: 'New Password', key: 'newPassword', placeholder: 'Min. 6 characters' },
              { label: 'Confirm New Password', key: 'confirmPassword', placeholder: 'Repeat new password' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: '600', fontSize: '0.875rem', color: 'var(--text-primary)', marginBottom: '0.4rem' }}>{f.label}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={show[f.key] ? 'text' : 'password'}
                    value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    required
                    style={{ width: '100%', padding: '0.75rem 2.75rem 0.75rem 1rem', border: '1px solid var(--border-color)', borderRadius: '8px', fontSize: '0.875rem', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShow(s => ({ ...s, [f.key]: !s[f.key] }))}
                    aria-label={show[f.key] ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                    style={{ position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', padding: '0.25rem' }}
                  >
                    <EyeIcon open={show[f.key]} />
                  </button>
                </div>
              </div>
            ))}

            <button
              type="submit"
              disabled={loading}
              style={{ width: '100%', padding: '0.875rem', background: 'linear-gradient(135deg, #667eea, #764ba2)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, marginTop: '0.5rem' }}
            >
              {loading ? 'Saving...' : 'Set Password'}
            </button>
          </form>
        )}

        <p style={{ textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          <button
            onClick={() => navigate('/login', { replace: true })}
            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            Back to login
          </button>
        </p>
      </div>
    </div>
  );
};

export default ForcePasswordReset;
